import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Plus, Trash2, Lock } from "lucide-react";
import { toast } from "sonner";

const PERM_LABELS = {
  manage_users: "Manage users",
  manage_roles: "Manage roles",
  create_opportunities: "Create opportunities",
  edit_estimations: "Edit estimations",
  delete_opportunities: "Delete opportunities",
  use_ai: "Use AI (generate & chat)",
  export_excel: "Export Excel",
};

export default function RolesTab() {
  const [roles, setRoles] = useState(null);
  const [form, setForm] = useState({ name: "", description: "" });

  const load = () => api.get("/admin/roles").then((r) => setRoles(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const allPerms = roles?.[0]?.all_permissions || Object.keys(PERM_LABELS);

  const createRole = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/roles", { ...form, permissions: [] });
      setForm({ name: "", description: "" });
      toast.success("Role created");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const togglePerm = async (role, perm) => {
    const permissions = role.permissions.includes(perm)
      ? role.permissions.filter((p) => p !== perm)
      : [...role.permissions, perm];
    setRoles(roles.map((r) => (r.id === role.id ? { ...r, permissions } : r)));
    try { await api.put(`/admin/roles/${role.id}`, { permissions }); }
    catch (e) { toast.error(apiError(e)); load(); }
  };

  const remove = async (role) => {
    if (!window.confirm(`Delete role "${role.name}"?`)) return;
    try { await api.delete(`/admin/roles/${role.id}`); setRoles(roles.filter((r) => r.id !== role.id)); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!roles) return <p className="text-sm text-zinc-500">Loading…</p>;

  return (
    <div data-testid="admin-roles-tab" className="space-y-6">
      <form onSubmit={createRole} className="flex flex-wrap items-center gap-2">
        <input data-testid="new-role-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="New role name (e.g. sales)" required
          className="border border-zinc-300 rounded-sm px-3 py-2 text-sm w-56 outline-none focus:border-[#002FA7]" />
        <input data-testid="new-role-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
          placeholder="Description"
          className="border border-zinc-300 rounded-sm px-3 py-2 text-sm w-80 outline-none focus:border-[#002FA7]" />
        <button data-testid="create-role-button" className="flex items-center gap-1.5 bg-[#002FA7] hover:bg-[#002280] text-white text-sm font-medium px-4 py-2 rounded-sm">
          <Plus size={14} /> Add role
        </button>
      </form>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {roles.map((role) => {
          const locked = role.name === "admin";
          return (
            <div key={role.id} data-testid={`role-card-${role.name}`} className="bg-white border border-zinc-200 rounded-sm">
              <div className="px-4 py-3 border-b border-zinc-200 flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-medium flex items-center gap-1.5">
                    {role.name}
                    {locked && <Lock size={11} className="text-zinc-400" />}
                    {role.built_in && <span className="text-[9px] uppercase tracking-wide bg-zinc-100 text-zinc-500 px-1.5 py-0.5 rounded-sm">built-in</span>}
                  </p>
                  <p className="text-xs text-zinc-400 mt-0.5">{role.description || "—"}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-[11px] font-mono text-zinc-400">{role.user_count} user{role.user_count === 1 ? "" : "s"}</span>
                  {!role.built_in && (
                    <button data-testid={`delete-role-${role.name}`} onClick={() => remove(role)} className="text-zinc-300 hover:text-red-600 p-1">
                      <Trash2 size={13} strokeWidth={1.5} />
                    </button>
                  )}
                </div>
              </div>
              <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {allPerms.map((perm) => (
                  <label key={perm} className={`flex items-center gap-2 text-xs ${locked ? "opacity-60" : "cursor-pointer"}`}>
                    <input type="checkbox" data-testid={`perm-${role.name}-${perm}`} disabled={locked}
                      checked={locked || role.permissions.includes(perm)}
                      onChange={() => togglePerm(role, perm)}
                      className="accent-[#002FA7]" />
                    {PERM_LABELS[perm] || perm}
                  </label>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
